/** UI copy for the director surfaces, English + Hindi. Game facts come from the server, not here. */
import type { DirectorLanguage, DirectorMoment } from '@clutch/core';
import { isChineseLanguage, pick } from './i18n';

const EN = {
  catchUp: 'Catch me up',
  sixty: '60 sec',
  listen: 'Listen',
  listening: 'Listening',
  stop: 'Mute',
  replayLabel: 'Replay · real NBA play-by-play',
  liveLabel: 'Live · real play-by-play',
  fallback: 'Selected by Clutch rules',
  model: 'AI-picked moments',
  written: 'AI-written · verified game events',
  loading: 'Finding the moments that matter to you…',
  busy: 'Clutch is busy writing other stories. Try again in a few seconds.',
  failed: 'Couldn’t build your catch-up. Try again.',
  empty: 'Nothing has happened yet. Your story starts at tip-off.',
  retry: 'Try again',
  close: 'Close',
  replay: 'Watch again',
  makeStory: 'Make it a story',
  rematch: 'Rematch on the next game',
  backToRoom: 'Back to the room',
  watchFinish: 'Watch the finish · make your call',
  friend: 'Your friend’s version',
  final: 'Final',
  asOf: 'Score now',
  calledAt: 'Called',
  tapHint: 'Tap → next · hold to pause',
  noVoice: 'Narration is unavailable. Read the captions or try again.',
  paused: 'Paused',
  intro: 'Your story',
  outcome: 'How it ended',
  voiceAi: 'AI voice',
  previous: 'Previous', next: 'Next', play: 'Play', pause: 'Pause', language: 'Language',
  missingTranslation: 'This story is not yet available in this language.',
  serverRequired: 'Catch-ups need the Clutch server.',
} as const;
type Strings = { [K in keyof typeof EN]: string };

const HI: Strings = {
  catchUp: 'मुझे अपडेट करें',
  sixty: '60 सेकंड',
  listen: 'सुनिए',
  listening: 'सुन रहे हैं',
  stop: 'म्यूट',
  replayLabel: 'रीप्ले · असली NBA प्ले-बाय-प्ले',
  liveLabel: 'लाइव · असली प्ले-बाय-प्ले',
  fallback: 'Clutch नियमों से चुने गए पल',
  model: 'AI ने चुने पल',
  written: 'AI की कहानी · सत्यापित मैच घटनाएँ',
  loading: 'आपके लिए अहम पल ढूँढ रहे हैं…',
  busy: 'Clutch अभी दूसरी कहानियाँ लिख रहा है। कुछ सेकंड में फिर कोशिश करें।',
  failed: 'आपका अपडेट नहीं बन सका। फिर कोशिश करें।',
  empty: 'अभी कुछ नहीं हुआ है। आपकी कहानी टिप-ऑफ़ से शुरू होगी।',
  retry: 'फिर कोशिश करें',
  close: 'बंद करें',
  replay: 'फिर से देखें',
  makeStory: 'इसे स्टोरी बनाइए',
  rematch: 'अगले मैच में बदला',
  backToRoom: 'रूम में लौटें',
  watchFinish: 'आख़िरी मिनट देखें · अपनी कॉल लगाइए',
  friend: 'आपके दोस्त का नज़रिया',
  final: 'अंतिम',
  asOf: 'अभी का स्कोर',
  calledAt: 'कॉल',
  tapHint: 'टैप → अगला · रोकने के लिए दबाए रखें',
  noVoice: 'अभी आवाज़ उपलब्ध नहीं है। कैप्शन पढ़ें या फिर कोशिश करें।',
  paused: 'रुका हुआ',
  intro: 'आपकी कहानी',
  outcome: 'नतीजा',
  voiceAi: 'AI आवाज़',
  previous: 'पिछला', next: 'अगला', play: 'चलाएँ', pause: 'रोकें', language: 'भाषा',
  missingTranslation: 'यह कहानी अभी इस भाषा में उपलब्ध नहीं है।',
  serverRequired: 'कैच-अप के लिए Clutch सर्वर चाहिए।',
};

const ZH: Strings = {
  catchUp: '帮我补课',
  sixty: '60 秒',
  listen: '收听',
  listening: '收听中',
  stop: '静音',
  replayLabel: '回放 · 真实NBA文字直播',
  liveLabel: '直播 · 真实文字直播',
  fallback: '由Clutch规则挑选',
  model: 'AI挑选的时刻',
  written: 'AI撰写 · 已核实的比赛事件',
  loading: '正在寻找对你重要的时刻…',
  busy: 'Clutch正忙着写别的故事，请稍后再试。',
  failed: '无法生成你的补课，请重试。',
  empty: '比赛还没开始，你的故事从跳球开始。',
  retry: '重试',
  close: '关闭',
  replay: '再看一遍',
  makeStory: '生成故事',
  rematch: '下一场再战',
  backToRoom: '回到房间',
  watchFinish: '观看结尾 · 做出你的预测',
  friend: '你朋友的版本',
  final: '终场',
  asOf: '当前比分',
  calledAt: '预测于',
  tapHint: '点击 → 下一段 · 长按暂停',
  noVoice: '语音暂不可用。请阅读字幕或重试。',
  paused: '已暂停',
  intro: '你的故事',
  outcome: '最终结果',
  voiceAi: 'AI语音',
  previous: '上一段', next: '下一段', play: '播放', pause: '暂停', language: '语言',
  missingTranslation: '此故事暂时没有中文翻译。请选择另一种语言或稍后重试。',
  serverRequired: '精彩回顾需要连接Clutch服务器。',
};

export function strings(l: DirectorLanguage | 'zh' | string): Strings {
  return l === 'hi' ? HI : isChineseLanguage(l) ? ZH : EN;
}

const KIND: Record<DirectorMoment['kind'], { en: string; hi: string; zh: string }> = {
  score: { en: 'Bucket', hi: 'स्कोर', zh: '得分' },
  run: { en: 'The run', hi: 'रन', zh: '得分潮' },
  lead_change: { en: 'Lead change', hi: 'बढ़त बदली', zh: '领先易主' },
  comeback: { en: 'Comeback', hi: 'वापसी', zh: '逆转' },
  player: { en: 'Your player', hi: 'आपका खिलाड़ी', zh: '你的球员' },
  call: { en: 'Your call', hi: 'आपकी कॉल', zh: '你的预测' },
  reaction: { en: 'Your reaction', hi: 'आपका रिएक्शन', zh: '你的反应' },
  finish: { en: 'The finish', hi: 'अंत', zh: '终局' },
};
export const kindLabel = (k: DirectorMoment['kind'], l: DirectorLanguage | 'zh') => pick(KIND[k], l);

/** Greeting by local time of day. */
export function greeting(l: DirectorLanguage | 'zh', hour = new Date().getHours()): string {
  if (isChineseLanguage(l)) return hour < 12 ? '早上好。' : hour < 18 ? '下午好。' : '晚上好。';
  if (l === 'hi') return hour < 12 ? 'सुप्रभात।' : hour < 17 ? 'नमस्ते।' : 'शुभ संध्या।';
  return hour < 5 ? 'Late one.' : hour < 12 ? 'Morning.' : hour < 17 ? 'Afternoon.' : 'Evening.';
}
